import React, { useState } from 'react';
import { Card, FormLayout, TextField, Button, Banner, BlockStack, Text } from '@shopify/polaris';
import { useAuth } from '../contexts/AuthContext';
import zemtrixLogo from '../assets/zemtrix-logo.png';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [useOtp, setUseOtp] = useState(false);
  const [otpRequested, setOtpRequested] = useState(false);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const { login, requestOtp, loginWithOtp } = useAuth();

  const resetOtpState = () => {
    setOtpRequested(false);
    setCode('');
    setError('');
    setInfo('');
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(username, password);
    if (!result.success) {
      setError(result.error);
    }

    setLoading(false);
  };

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setInfo('');

    const result = await requestOtp(username);
    if (result.success) {
      setOtpRequested(true);
      setInfo('If that account exists, a login code has been emailed to it.');
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await loginWithOtp(username, code);
    if (!result.success) {
      setError(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="zx-login-page">
      <div className="zx-login-card-wrap">
        <Card padding="0">
          <div className="zx-login-accent-bar" />
          <div className="zx-login-card-logo">
            <img src={zemtrixLogo} alt="Zemtrix" />
          </div>
          <div style={{ padding: 'var(--p-space-600)' }}>
            <BlockStack gap="500">
              <BlockStack gap="100">
                <Text as="h2" variant="headingMd">
                  Sign in
                </Text>
                <Text as="p" tone="subdued">
                  {useOtp
                    ? otpRequested
                      ? 'Enter the code that was emailed to you'
                      : 'Enter your username to receive a login code'
                    : 'Enter your username and password to continue'}
                </Text>
              </BlockStack>

              {error && <Banner tone="critical">{error}</Banner>}
              {info && !error && <Banner tone="info">{info}</Banner>}

              {!useOtp && (
                <form onSubmit={handlePasswordSubmit}>
                  <FormLayout>
                    <TextField
                      label="Username"
                      value={username}
                      onChange={setUsername}
                      autoComplete="username"
                      placeholder="Enter your username"
                      disabled={loading}
                    />
                    <TextField
                      label="Password"
                      type="password"
                      value={password}
                      onChange={setPassword}
                      autoComplete="current-password"
                      placeholder="Enter your password"
                      disabled={loading}
                    />
                    <Button variant="primary" fullWidth submit loading={loading} disabled={!username || !password}>
                      Login
                    </Button>
                    <Button
                      variant="tertiary"
                      fullWidth
                      disabled={loading}
                      onClick={() => {
                        setUseOtp(true);
                        resetOtpState();
                      }}
                    >
                      Log in with an emailed code instead
                    </Button>
                  </FormLayout>
                </form>
              )}

              {useOtp && !otpRequested && (
                <form onSubmit={handleRequestCode}>
                  <FormLayout>
                    <TextField
                      label="Username"
                      value={username}
                      onChange={setUsername}
                      autoComplete="username"
                      placeholder="Enter your username"
                      disabled={loading}
                    />
                    <Button variant="primary" fullWidth submit loading={loading} disabled={!username}>
                      Send login code
                    </Button>
                    <Button
                      variant="tertiary"
                      fullWidth
                      disabled={loading}
                      onClick={() => {
                        setUseOtp(false);
                        resetOtpState();
                      }}
                    >
                      Back to password login
                    </Button>
                  </FormLayout>
                </form>
              )}

              {useOtp && otpRequested && (
                <form onSubmit={handleVerifyCode}>
                  <FormLayout>
                    <TextField
                      label="Login code"
                      value={code}
                      onChange={setCode}
                      autoComplete="one-time-code"
                      placeholder="6-digit code"
                      disabled={loading}
                    />
                    <Button variant="primary" fullWidth submit loading={loading} disabled={!code}>
                      Verify and log in
                    </Button>
                    <Button variant="tertiary" fullWidth disabled={loading} onClick={resetOtpState}>
                      Request a new code
                    </Button>
                  </FormLayout>
                </form>
              )}
            </BlockStack>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Login;
